import { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

// Token Mapbox lu depuis l'env de build (CRA : préfixe REACT_APP_).
// Si vide → fallback OSM/Carto (gratuit). Cf. admin/.env
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Centre Côte d'Ivoire + zoom initial
const CI_CENTER = [7.54, -5.55];
const CI_ZOOM = 7;

// Icônes custom (SVG inline pour ne pas dépendre des assets Leaflet)
const makeIcon = (color, emoji) => L.divIcon({
  html: `<div style="background:${color};width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:14px;">${emoji}</div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const ICON_SERVICE  = makeIcon('#F59E0B', '💼');
const ICON_CLIENT   = makeIcon('#3B82F6', '🛒');
const ICON_PROVIDER = makeIcon('#10B981', '🔧');

// Composant qui recadre la carte sur les points si on en a
function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => [Number(p.lat), Number(p.lng)]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }, [points, map]);
  return null;
}

export default function Heatmap() {
  const [data, setData] = useState({ services_points: [], clients_points: [], providers_points: [], by_commune: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState({ services: true, clients: true, providers: true });

  useEffect(() => {
    api.get('/admin/heatmap')
      .then(({ data }) => setData({
        ...data,
        services_points: data.services_points || data.points || [],
        clients_points: data.clients_points || [],
        providers_points: data.providers_points || [],
      }))
      .finally(() => setLoading(false));
  }, []);

  const allPoints = useMemo(() => [
    ...(show.services  ? data.services_points  : []),
    ...(show.clients   ? data.clients_points   : []),
    ...(show.providers ? data.providers_points : []),
  ], [data, show]);

  const totalRevenue = data.by_commune.reduce((s, c) => s + Number(c.total_revenue || 0), 0);
  const stats = data.stats || {};

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}><div className="spinner" /></div>;

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>🗺️ Carte Côte d'Ivoire — Acteurs géolocalisés</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {stats.services || 0} annonces · {stats.clients || 0} clients · {stats.providers || 0} prestataires localisés
        </p>
      </div>

      {/* Toggles visibles */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Toggle active={show.services} onClick={() => setShow(s => ({ ...s, services: !s.services }))} color="#F59E0B" emoji="💼" label="Annonces" count={data.services_points.length} />
        <Toggle active={show.clients}  onClick={() => setShow(s => ({ ...s, clients: !s.clients }))}   color="#3B82F6" emoji="🛒" label="Clients"  count={data.clients_points.length} />
        <Toggle active={show.providers} onClick={() => setShow(s => ({ ...s, providers: !s.providers }))} color="#10B981" emoji="🔧" label="Prestataires" count={data.providers_points.length} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.2fr) minmax(280px, 1fr)', gap: 16 }}>
        {/* Vraie carte Leaflet + OpenStreetMap */}
        <div className="card" style={{ height: 580, overflow: 'hidden' }}>
          <MapContainer center={CI_CENTER} zoom={CI_ZOOM} style={{ height: '100%', width: '100%', borderRadius: 8 }}>
            <LayersControl position="topright">
              {MAPBOX_TOKEN ? (
                <>
                  {/* Bases Mapbox (premium, nécessite un token) */}
                  <LayersControl.BaseLayer checked name="Mapbox Streets">
                    <TileLayer
                      attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/about/">OpenStreetMap</a>'
                      url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                      tileSize={512}
                      zoomOffset={-1}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Mapbox Satellite">
                    <TileLayer
                      attribution='&copy; Mapbox'
                      url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                      tileSize={512}
                      zoomOffset={-1}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Mapbox Sombre">
                    <TileLayer
                      attribution='&copy; Mapbox'
                      url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                      tileSize={512}
                      zoomOffset={-1}
                    />
                  </LayersControl.BaseLayer>
                </>
              ) : (
                <>
                  {/* Fallback gratuit OSM si pas de token Mapbox */}
                  <LayersControl.BaseLayer checked name="Plan standard (OSM)">
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satellite (Esri)">
                    <TileLayer
                      attribution='Tiles &copy; Esri'
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Sombre (Carto)">
                    <TileLayer
                      attribution='&copy; OpenStreetMap, &copy; CartoDB'
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
                    />
                  </LayersControl.BaseLayer>
                </>
              )}
            </LayersControl>

            <FitToPoints points={allPoints} />

            {/* Annonces */}
            {show.services && data.services_points.map(s => (
              <Marker key={`s-${s.id}`} position={[Number(s.lat), Number(s.lng)]} icon={ICON_SERVICE}>
                <Popup>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{s.category_icon} {s.title}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Catégorie : {s.category}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>📍 {s.commune || s.city || '—'}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Prestataire : <strong>{s.provider_name}</strong></div>
                </Popup>
              </Marker>
            ))}

            {/* Clients */}
            {show.clients && data.clients_points.map(c => (
              <Marker key={`c-${c.id}`} position={[Number(c.lat), Number(c.lng)]} icon={ICON_CLIENT}>
                <Popup>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>🛒 {c.name}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{c.phone}</div>
                  <div style={{ fontSize: 12 }}>📍 {c.commune || c.city || '—'}</div>
                </Popup>
              </Marker>
            ))}

            {/* Prestataires (sans annonce active visible) */}
            {show.providers && data.providers_points.map(p => (
              <Marker key={`p-${p.id}`} position={[Number(p.lat), Number(p.lng)]} icon={ICON_PROVIDER}>
                <Popup>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>🔧 {p.name}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{p.phone}</div>
                  <div style={{ fontSize: 12 }}>📍 {p.commune || p.city || '—'}</div>
                </Popup>
              </Marker>
            ))}

            {/* Zone de densité (cercles transparents superposés) */}
            {show.services && data.services_points.map(s => (
              <CircleMarker
                key={`heat-${s.id}`}
                center={[Number(s.lat), Number(s.lng)]}
                radius={20}
                pathOptions={{ color: '#F59E0B', weight: 0, fillColor: '#F59E0B', fillOpacity: 0.10 }}
              />
            ))}
          </MapContainer>
        </div>

        {/* Top communes */}
        <div className="card">
          <div className="card-body" style={{ maxHeight: 580, overflowY: 'auto' }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Top communes</h3>
            {data.by_commune.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>Aucune commune active</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.by_commune.slice(0, 20).map((c, i) => {
                  const pct = totalRevenue > 0 ? (Number(c.total_revenue) / totalRevenue) * 100 : 0;
                  return (
                    <div key={c.commune}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{i + 1}. {c.commune}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.services_count} ann. · {fmt(c.total_revenue)} F</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: 6, width: `${Math.max(pct, 3)}%`, background: '#F59E0B', transition: 'width 0.6s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Toggle = ({ active, onClick, color, emoji, label, count }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 14px',
      borderRadius: 22,
      background: active ? color : 'transparent',
      color: active ? '#FFFFFF' : 'var(--text-secondary)',
      border: `1.5px solid ${active ? color : 'var(--border)'}`,
      cursor: 'pointer',
      fontSize: 13, fontWeight: 700,
      transition: 'all 0.18s ease',
      opacity: active ? 1 : 0.7,
    }}
  >
    <span>{emoji}</span>
    <span>{label}</span>
    <span style={{
      background: active ? 'rgba(255,255,255,0.25)' : 'var(--surface-2)',
      padding: '1px 8px', borderRadius: 10, fontSize: 11,
    }}>{count}</span>
  </button>
);
